import React, { useState, useEffect, useRef } from "react";
import { Input, Select, DatePicker } from "antd";
import "./EventsFilters.css";

import { DownOutlined, SearchOutlined, UpOutlined } from "@ant-design/icons";
import {
  EVENTS_ALL_EVENT,
  EVENTS_ALL_REPORT,
  MEMBER_BY_EVENT_REPORT,
  MEMBER_DETAILS_REPORT,
} from "../../../../../constants/leaderboard";
import { isMobile } from "../../../../../utils/helper";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";

const EventsFilters = ({
  handleDropdownChange,
  showEventsFilter,
  eventsDropdownList,
  handleSearch,
  seasons,
  search,
  filters,
  eventsLoading,
  searchType,
}) => {
  const REPORTS_FILTERS = [
    MEMBER_BY_EVENT_REPORT,
    MEMBER_DETAILS_REPORT,
    EVENTS_ALL_REPORT,
  ];

  const { RangePicker } = DatePicker;

  const updatedSeasonsFilter =
    seasons?.map((item) => {
      return {
        value: item,
        label: <span>{item}</span>,
      };
    }) || [];

  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isWrapping, setIsWrapping] = useState(false);
  const filtersContainerRef = useRef(null);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters((prev) => !prev);
  };

  // Check if filters are wrapping
  useEffect(() => {
    const checkWrapping = () => {
      if (filtersContainerRef.current && !isMobile()) {
        const container = filtersContainerRef.current;

        // Count potential filters that should be visible
        let filterCount = 2; // Report and Season are always visible
        if (showEventsFilter) filterCount++;
        if (seasons?.length > 0 && filters.season) filterCount++;

        // Estimate space needed: each filter ~200px + gaps ~16px + more filters button ~120px
        const estimatedWidth = filterCount * 216 + 120;
        const containerWidth = container.getBoundingClientRect().width;

        // Simple check: if we don't have enough space, show more filters
        const shouldWrap = estimatedWidth > containerWidth;

        setIsWrapping(shouldWrap);
      }
    };

    // Delay check to ensure DOM is rendered
    const timeoutId = setTimeout(checkWrapping, 50);
    window.addEventListener("resize", checkWrapping);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkWrapping);
    };
  }, [filters, eventsDropdownList, seasons, showEventsFilter]);

  return (
    <div className="events-filters-container">
      {isMobile() ? (
        <div
          className="event-filters-header"
          onClick={toggleExpand}
          style={isExpanded ? { borderRadius: "0%" } : undefined}
        >
          <span>Filters</span>
          {isExpanded ? (
            <UpOutlined style={{ marginLeft: "auto" }} />
          ) : (
            <DownOutlined style={{ marginLeft: "auto" }} />
          )}
        </div>
      ) : null}

      {(isExpanded || !isMobile()) && (
        <div className="event-filters-options">
          {/* Search Input - Left side */}
          <div className="search-container">
            <Input
              placeholder={`Search ${searchType === "members" ? "name" : "event"}`}
              prefix={<SearchOutlined />}
              style={{ width: isMobile() ? "100%" : "15rem" }}
              onChange={(e) => handleSearch(e.target.value)}
              value={search}
              disabled={eventsLoading}
            />
          </div>

          {/* Filters Container - Right side */}
          <div className="filters-wrapper">
            {/* Primary Filters Row */}
            <div
              className="event-filters primary-filters"
              ref={filtersContainerRef}
            >
              <Select
                className="event-filter-input"
                defaultActiveFirstOption
                popupMatchSelectWidth={false}
                options={REPORTS_FILTERS?.map((item) => ({
                  value: item,
                  label: <span>{item}</span>,
                }))}
                value={filters.report}
                onChange={(val) => handleDropdownChange("report", val)}
                disabled={eventsLoading}
                loading={eventsLoading}
              />

              <Select
                className="event-filter-input"
                defaultActiveFirstOption
                popupMatchSelectWidth={false}
                options={updatedSeasonsFilter}
                value={filters.season}
                onChange={(val) => handleDropdownChange("season", val)}
                disabled={eventsLoading}
                loading={eventsLoading}
              />

              {/* Conditionally visible filters */}
              {showEventsFilter && !isWrapping && (
                <Select
                  className="event-filter-input"
                  defaultActiveFirstOption
                  popupMatchSelectWidth={false}
                  options={[
                    {
                      value: EVENTS_ALL_EVENT,
                      label: <span>{EVENTS_ALL_EVENT}</span>,
                    },
                    ...eventsDropdownList,
                  ]}
                  value={filters.event}
                  onChange={(val) => handleDropdownChange("event", val)}
                  disabled={eventsLoading}
                  loading={eventsLoading}
                />
              )}

              {/* Date Range - Conditionally visible when not wrapping */}
              {seasons?.length > 0 && filters.season && !isWrapping && (
                <RangePicker
                  className="event-filter-input"
                  placeholder={["Start Date", "End Date"]}
                  value={filters.dateRange}
                  onChange={(dates) => handleDropdownChange("dateRange", dates)}
                  disabled={eventsLoading}
                  allowClear={true}
                  style={{ width: isMobile() ? "100%" : "auto" }}
                />
              )}

              {/* More Filters Toggle - Show in same row when wrapping */}
              {(isWrapping || isMobile()) && (
                <ChantButton
                  type="text"
                  onClick={toggleAdvancedFilters}
                  className="more-filters-toggle"
                  style={{
                    padding: "4px 8px",
                    height: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    flexShrink: 0,
                  }}
                >
                  {showAdvancedFilters ? "Hide" : "More"} Filters
                  {showAdvancedFilters ? <UpOutlined /> : <DownOutlined />}
                </ChantButton>
              )}
            </div>

            {/* Advanced Filters - Hidden filters when wrapping */}
            {(isWrapping || isMobile()) && (
              <div
                className={`advanced-filters ${
                  showAdvancedFilters ? "expanded" : "collapsed"
                }`}
              >
                {showEventsFilter && (
                  <Select
                    className="event-filter-input"
                    defaultActiveFirstOption
                    popupMatchSelectWidth={false}
                    options={[
                      {
                        value: EVENTS_ALL_EVENT,
                        label: <span>{EVENTS_ALL_EVENT}</span>,
                      },
                      ...eventsDropdownList,
                    ]}
                    value={filters.event}
                    onChange={(val) => handleDropdownChange("event", val)}
                    disabled={eventsLoading}
                    loading={eventsLoading}
                  />
                )}

                {seasons?.length > 0 && filters.season && (
                  <RangePicker
                    className="event-filter-input"
                    placeholder={["Start Date", "End Date"]}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      handleDropdownChange("dateRange", dates)
                    }
                    disabled={eventsLoading}
                    allowClear={true}
                    style={{ width: isMobile() ? "100%" : "auto" }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsFilters;
